/**
 * Load self-hosted MapLibre vendor (``assets/ops-map-maplibre.js``).
 * Path is fixed relative to ``<base href>`` (``index.html`` / ``croc-dashboard-base``) — no runtime scan of ``app.js``.
 */

/** Relative to ``document.baseURI`` (dashboard mount, e.g. ``/console/``). */
export const OPS_MAP_VENDOR_REL_PATH = "assets/ops-map-maplibre.js";

/** Same-origin worker shipped next to the vendor bundle (CSP worker build). */
export const OPS_MAP_MAPLIBRE_WORKER_REL_PATH = "assets/maplibre-gl-csp-worker.js";

const ATTR_PRE_VENDOR = "data-croc-ops-preload-vendor";
const ATTR_PRE_WORKER = "data-croc-ops-preload-worker";
const ATTR_PC_ARCGIS = "data-croc-ops-preconnect-arcgis";
const ATTR_PC_ARCGIS_ONLINE = "data-croc-ops-preconnect-arcgisonline";
const ATTR_PC_OPENFREEMAP = "data-croc-ops-preconnect-openfreemap";
const ATTR_DNS_GEOCODE = "data-croc-ops-dns-geocode";

export function resolveOpsMapVendorScriptUrl() {
  if (typeof document === "undefined") return OPS_MAP_VENDOR_REL_PATH;
  try {
    return new URL(OPS_MAP_VENDOR_REL_PATH, document.baseURI).href;
  } catch (_) {
    return OPS_MAP_VENDOR_REL_PATH;
  }
}

export function resolveOpsMapMaplibreWorkerUrl() {
  if (typeof document === "undefined") return OPS_MAP_MAPLIBRE_WORKER_REL_PATH;
  try {
    return new URL(OPS_MAP_MAPLIBRE_WORKER_REL_PATH, document.baseURI).href;
  } catch (_) {
    return OPS_MAP_MAPLIBRE_WORKER_REL_PATH;
  }
}

/**
 * Injects ``<link rel="preload">`` / ``dns-prefetch`` once so the first Home map (``#/overview``) visit
 * avoids cold-cache fetches for vendor JS and tile/geocode hosts (worker uses low-priority prefetch).
 */
export function injectOpsMapAssetPreloads() {
  if (typeof document === "undefined" || !document.head) return;
  const head = document.head;

  const vendor = resolveOpsMapVendorScriptUrl();
  if (!head.querySelector(`link[${ATTR_PRE_VENDOR}]`)) {
    try {
      const link = document.createElement("link");
      link.rel = "preload";
      link.href = vendor;
      link.as = "script";
      link.setAttribute(ATTR_PRE_VENDOR, "");
      head.appendChild(link);
    } catch (_) {}
  }

  const worker = resolveOpsMapMaplibreWorkerUrl();
  if (!head.querySelector(`link[${ATTR_PRE_WORKER}]`)) {
    /** ``as=worker`` is not a supported preload destination in Chromium/Safari — skip to avoid console noise. */
    try {
      const w = document.createElement("link");
      w.rel = "prefetch";
      w.href = worker;
      w.setAttribute(ATTR_PRE_WORKER, "");
      head.appendChild(w);
    } catch (_) {}
  }

  if (!head.querySelector(`link[${ATTR_PC_ARCGIS}]`)) {
    try {
      const p = document.createElement("link");
      p.rel = "preconnect";
      p.href = "https://tiles.arcgis.com";
      p.crossOrigin = "";
      p.setAttribute(ATTR_PC_ARCGIS, "");
      head.appendChild(p);
    } catch (_) {}
  }
  if (!head.querySelector(`link[${ATTR_PC_ARCGIS_ONLINE}]`)) {
    try {
      const q = document.createElement("link");
      q.rel = "preconnect";
      q.href = "https://server.arcgisonline.com";
      q.crossOrigin = "";
      q.setAttribute(ATTR_PC_ARCGIS_ONLINE, "");
      head.appendChild(q);
    } catch (_) {}
  }
  if (!head.querySelector(`link[${ATTR_PC_OPENFREEMAP}]`)) {
    try {
      const ofm = document.createElement("link");
      ofm.rel = "preconnect";
      ofm.href = "https://tiles.openfreemap.org";
      ofm.crossOrigin = "";
      ofm.setAttribute(ATTR_PC_OPENFREEMAP, "");
      head.appendChild(ofm);
    } catch (_) {}
  }
  if (!head.querySelector(`link[${ATTR_DNS_GEOCODE}]`)) {
    try {
      const d = document.createElement("link");
      d.rel = "dns-prefetch";
      d.href = "https://geocoding-api.open-meteo.com";
      d.setAttribute(ATTR_DNS_GEOCODE, "");
      head.appendChild(d);
    } catch (_) {}
  }
}

export function loadVendorScriptOnce(src) {
  const s = String(src || "");
  if (!s) return Promise.reject(new Error("missing vendor script src"));
  if (typeof window !== "undefined" && window.maplibregl) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const tag = document.createElement("script");
    tag.src = s;
    tag.async = true;
    tag.onload = () => resolve();
    tag.onerror = () => reject(new Error("ops-map vendor script load failed"));
    document.head.appendChild(tag);
  });
}

export async function ensureMapLibreLoaded(log) {
  if (typeof window !== "undefined" && window.maplibregl) {
    if (typeof log === "function") log("ensureMapLibreLoaded: already present");
    return;
  }
  try {
    injectOpsMapAssetPreloads();
  } catch (_) {}
  const url = resolveOpsMapVendorScriptUrl();
  if (typeof log === "function") log("ensureMapLibreLoaded: vendor", url);
  await loadVendorScriptOnce(url);
  if (typeof log === "function") log("ensureMapLibreLoaded: done");
}
